<template>
    <div
        :tabindex="searchable ? -1 : tabindex"
        :class="{ 'multiselect--active': isOpen, 'multiselect--disabled': disabled, 'multiselect--above': isAbove }"
        @focus="activate()"
        @mousedown="handleClickOutside"
        @keydown.self.down.prevent="pointerForward()"
        @keydown.self.up.prevent="pointerBackward()"
        @keypress.enter.tab.stop.self="addPointerElement($event)"
        @keyup.esc="deactivate()"
        class="multiselect">
        <slot name="caret" :toggle="toggle">
            <div @mousedown.prevent.stop="toggle()" class="multiselect__select"></div>
        </slot>
        <slot name="clear" :search="search"></slot>
        <div ref="tags" class="multiselect__tags">
            <span
                v-if="isPlaceholderVisible"
                class="multiselect__placeholder"
                @mousedown.prevent="toggle"
            >
                <slot name="placeholder">
                {{ placeholder }}
                </slot>
            </span>
            <span
                v-if="isSingleLabelVisible"
                class="multiselect__single"
                @mousedown.prevent="toggle"
            >
                  <slot name="singleLabel" :option="singleValue">
                     <template>{{ currentOptionLabel }}</template>
                  </slot>
            </span>
        </div>
        <transition name="multiselect">
            <div
                class="multiselect__content-wrapper"
                v-show="isOpen"
                @focus="activate"
                tabindex="-1"
                @mousedown.stop
                :style="{ maxHeight: optimizedHeight + 'px' }"
                ref="list"
            >
                <draggable
                    :list="options"
                    class="list-group"
                    ghost-class="ghost"
                    @end="select"
                >
                    <div
                        class="list-group-item"
                        v-for="element in options"
                        :key="trackBy ? element[trackBy] : element"
                        style="padding: 2px; display: flex; align-items: center;"
                    >
                        <div style="margin-right: 10px;">
                            <i class="icon icon-drag-dots draggable-handler"></i>
                        </div>
                        <div>
                            <b-form-checkbox @change="select(element)" v-model="element[toggleKey]"/>
                        </div>
                        <div class='default-font'>
                            {{ getOptionLabel(element) }}
                        </div>
                        <br>
                    </div>
                </draggable>
            </div>
        </transition>

    </div>
</template>

<script>
import multiselectMixin from './multiselectMixin'
import pointerMixin from './pointerMixin'
import VueDraggable from 'vuedraggable'


export default {
    name: 'vue-multiselect',
    components: {
        'draggable': VueDraggable
    },
    mixins: [multiselectMixin, pointerMixin],
    props: {
        /**
         * name attribute to match optional label element
         * @default ''
         * @type {String}
         */
        name: {
            type: String,
            default: ''
        },
        /**
         * String to show when pointing to an option
         * @default 'Press enter to select'
         * @type {String}
         */
        selectLabel: {
            type: String,
            default: 'Press enter to select'
        },
        /**
         * String to show when pointing to an option
         * @default 'Press enter to select'
         * @type {String}
         */
        selectGroupLabel: {
            type: String,
            default: 'Press enter to select group'
        },
        /**
         * String to show next to selected option
         * @default 'Selected'
         * @type {String}
         */
        selectedLabel: {
            type: String,
            default: 'Selected'
        },
        /**
         * String to show when pointing to an already selected option
         * @default 'Press enter to remove'
         * @type {String}
         */
        deselectLabel: {
            type: String,
            default: 'Press enter to remove'
        },
        /**
         * String to show when pointing to an already selected option
         * @default 'Press enter to remove'
         * @type {String}
         */
        deselectGroupLabel: {
            type: String,
            default: 'Press enter to deselect group'
        },
        /**
         * Decide whether to show pointer labels
         * @default true
         * @type {Boolean}
         */
        showLabels: {
            type: Boolean,
            default: true
        },
        /**
         * Limit the display of selected options. The rest will be hidden within the limitText string.
         * @default 99999
         * @type {Integer}
         */
        limit: {
            type: Number,
            default: 99999
        },
        /**
         * Sets maxHeight style value of the dropdown
         * @default 300
         * @type {Integer}
         */
        maxHeight: {
            type: Number,
            default: 300
        },
        /**
         * Function that process the message shown when selected
         * elements pass the defined limit.
         * @default 'and * more'
         * @param {Int} count Number of elements more than limit
         * @type {Function}
         */
        limitText: {
            type: Function,
            default: count => `and ${count} more`
        },
        /**
         * Set true to trigger the loading spinner.
         * @default False
         * @type {Boolean}
         */
        loading: {
            type: Boolean,
            default: false
        },
        /**
         * Disables the multiselect if true.
         * @default false
         * @type {Boolean}
         */
        disabled: {
            type: Boolean,
            default: false
        },
        /**
         * Fixed opening direction
         * @default ''
         * @type {String}
         */
        openDirection: {
            type: String,
            default: ''
        },
        /**
         * Shows slot with message about empty options
         * @default true
         * @type {Boolean}
         */
        showNoOptions: {
            type: Boolean,
            default: true
        },
        showNoResults: {
            type: Boolean,
            default: true
        },
        tabindex: {
            type: Number,
            default: 0
        }
    },
    computed: {
        isSingleLabelVisible () {
            return (
                (!this.isOpen || !this.searchable) &&
                this.options.filter(o => o.active).length
            )
        },
        isPlaceholderVisible () {
            return !this.internalValue.length && (!this.searchable || !this.isOpen) && !this.isSingleLabelVisible
        },
        visibleValues () {
            return this.multiple ? this.internalValue.slice(0, this.limit) : []
        },
        singleValue () {
            return this.internalValue[0]
        },
        deselectLabelText () {
            return this.showLabels ? this.deselectLabel : ''
        },
        deselectGroupLabelText () {
            return this.showLabels ? this.deselectGroupLabel : ''
        },
        selectLabelText () {
            return this.showLabels ? this.selectLabel : ''
        },
        selectGroupLabelText () {
            return this.showLabels ? this.selectGroupLabel : ''
        },
        selectedLabelText () {
            return this.showLabels ? this.selectedLabel : ''
        },
        inputStyle() {
            if (
                this.searchable ||
                (this.multiple && this.value && this.value.length)
            ) {
                // Hide input by setting the width to 0 allowing it to receive focus
                return this.isOpen
                    ? { width: '100%' }
                    : { width: '0', position: 'absolute', padding: '0' }
            }
            return false
        },
        contentStyle () {
            return this.options.length
                ? { display: 'inline-block' }
                : { display: 'block' }
        },
        isAbove () {
            if (this.openDirection === 'above' || this.openDirection === 'top') {
                return true
            } else if (
                this.openDirection === 'below' ||
                this.openDirection === 'bottom'
            ) {
                return false
            } else {
                return this.preferredOpenDirection === 'above'
            }
        },
        showSearchInput () {
            return (
                this.searchable &&
                (this.hasSingleSelectedSlot &&
                (this.visibleSingleValue || this.visibleSingleValue === 0)
                    ? this.isOpen
                    : true)
            )
        }
    }
}
</script>
